import { type ClassValue, clsx } from 'clsx';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { CountryCode } from 'libphonenumber-js';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function currencyFormat(num?: number) {
  return (num ?? 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export async function getFileFromImage(b64img: string, fileName: string) {
  return urltoFile(b64img, fileName, base64MimeType(b64img));
}

// return a promise that resolves with a File instance
function urltoFile(url, filename, mimeType) {
  return fetch(url)
    .then((res) => res.arrayBuffer())
    .then((buf) => new File([buf], filename, { type: mimeType }));
}

// return mime Type of bs64
function base64MimeType(encoded) {
  let result = '';

  if (typeof encoded !== 'string') {
    return result;
  }

  const mime = encoded.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);

  if (mime && mime.length) {
    result = mime[1]!;
  }

  return result;
}

export function slugToTitleCase(slug: string) {
  const separators = ['-', ' ', '_'];
  const separatorRegex = new RegExp(`[${separators.join('')}]`, 'g');
  const words = slug.split(separatorRegex);
  return words
    .map((word) => {
      if (word.length === 0) return ''; // Handle cases with consecutive separators
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function generateArrayOfDays(total: number, count: number): number[] {
  let trackBy = Number(total);
  const items: number[] = [];
  while (trackBy > 0) {
    items.push(count);
    trackBy -= count;
  }
  return items;
}

export enum DateScheme {
  base = 'eo, MMM yyyy',
}

export function formatDate(date: Date, scheme: DateScheme = DateScheme.base) {
  return format(date, scheme);
}

export const REGIONS_IN_GHANA = [
  'Ahafo',
  'Ashanti',
  'Bono',
  'Bono East',
  'Central',
  'Eastern',
  'Greater Accra',
  'North East',
  'Northern',
  'Oti',
  'Savannah',
  'Upper East',
  'Upper West',
  'Volta',
  'Western North',
  'Western',
];

export const EMPLOYMENTS = [
  'Agriculture',
  'Education',
  'Healthcare',
  'Finance',
  'Construction',
  'Manufacturing',
  'Mining',
  'Public service',
  'Retail',
  'Technology',
  'Transportation',
  'Tourism',
  'Telecommunications',
  'Energy',
  'Media',
  'Legal',
  'Hospitality',
  'Real estate',
  'NGO',
  'Security',
];

export const EMPLOYMENTS_STATUS = [
  'Employed',
  'Self-Employed',
  'Unemployed',
  'Student',
  'Retired',
  'Freelancer',
  'Homemaker',
  'Contractor',
  'Intern',
  'Part time',
  'Seasonal',
];

export interface PhoneNumberBreakdown {
  countryCode: CountryCode;
  areaCode: string;
  number: string;
  formatted: string;
  isValid: boolean;
  cleaned: string;
}

export function capitalizeFirstLetter(value: string): string {
  return value;
  // return (value ?? '     ').charAt(0).toUpperCase() + this.slice(1);
}

export function parsePhoneNumber(phoneNumber: string): PhoneNumberBreakdown {
  // Remove all non-digit characters (including spaces)
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Default breakdown object
  const breakdown: PhoneNumberBreakdown = {
    countryCode: 'GH',
    areaCode: '',
    number: '',
    formatted: phoneNumber,
    isValid: false,
    cleaned: cleaned.toString(),
  };

  // Check if it's a Ghana number (+233)
  if (cleaned.startsWith('233') && cleaned.length === 12) {
    breakdown.countryCode = 'GH';
    breakdown.areaCode = cleaned.substring(3, 5); // Next 2 digits after 233
    breakdown.number = cleaned.substring(3); // Remaining digits
    breakdown.formatted = `+233 ${
      breakdown.areaCode
    } ${breakdown.number.substring(0, 3)} ${breakdown.number.substring(3)}`;
    breakdown.isValid = true;
  }
  // Handle other common formats or add more country codes as needed
  else if (cleaned.length >= 10) {
    // Generic fallback for other international numbers
    if (cleaned.length > 10) {
      const countryCodeLength = cleaned.length - 10;
      breakdown.countryCode = 'GH'; // Generic country code
      breakdown.areaCode = cleaned.substring(
        countryCodeLength,
        countryCodeLength + 3
      );
      breakdown.number = cleaned.substring(countryCodeLength + 3);
      breakdown.formatted = `+${cleaned.substring(0, countryCodeLength)} ${
        breakdown.areaCode
      } ${breakdown.number}`;
      breakdown.isValid = true;
    }
  }

  return breakdown;
}

export function getNetworkLogo(type: string): string | null {
  const logos: Record<string, string> = {
    mtn_mobile_money: '/assets/logos/mtn.png',
    telecel_mobile_money: '/assets/logos/telecel.png',
  };
  type = Object.keys(logos).find(k => k.includes(type.toLowerCase())) || type;
  return logos[type] || null;
}